// Per-teacher sidebar group visibility.
//
// Schema (on user.profile):
//   - sidebarGroupsHidden?: string[]   Names of nav groups the teacher
//                                       has chosen to hide. Persisted on
//                                       the user profile so the choice
//                                       follows them across devices.
//
// Two layers of "hidden":
//   1. FROZEN_HIDDEN — groups hard-hidden in code (not yet ready for
//      teacher exposure). Currently Dolch and Nonsense Words. The teacher
//      cannot un-hide these.
//   2. TOGGLEABLE — groups the teacher controls via Assessment Settings
//      → Visible Assessment Types. Defaults reflect today's behavior:
//      HFW visible, CVC visible.

export const TOGGLEABLE_GROUPS = ['High Frequency Words', 'CVC Words', '4K Readiness'];

// Hard-hidden in code until smoke-tested with real teachers.
// To expose one of these later, move it from FROZEN_HIDDEN into
// TOGGLEABLE_GROUPS — the toggle will appear on Assessment Settings
// automatically.
export const FROZEN_HIDDEN = ['Dolch', 'Nonsense Words'];

// Default hidden state for new teachers — empty (everything toggleable
// is visible by default). Dolch / NW handled separately via FROZEN_HIDDEN.
export const DEFAULT_HIDDEN = [];

// Optional teacher-facing descriptions for each toggleable group.
// Used by the Visible Assessment Types card in Assessment Settings.
export const GROUP_DESCRIPTIONS = {
  'High Frequency Words':
    'Sight words students should recognize without sounding them out — words like "the," "and," "see." Useful from late Kindergarten through Grade 1.',
  'CVC Words':
    'Three-letter words students decode by sounding them out — words like "cat," "sun," "dog." Used for early decoding practice from Pre-K through Grade 1, or as intervention for older students.',
  '4K Readiness':
    'Optional one-time start-of-year readiness checks: naming geometric shapes, numerals 1–10, and colors. Each student is checked once per school year — hide this section if your class doesn\'t use it.',
};

/**
 * Returns true if the given nav group should be visible for this profile.
 * Hides FROZEN_HIDDEN groups unconditionally; hides TOGGLEABLE groups
 * the teacher has explicitly opted out of; shows everything else.
 */
export function isGroupVisible(profile, groupName) {
  if (FROZEN_HIDDEN.includes(groupName)) return false;
  const hidden = profile?.sidebarGroupsHidden;
  if (Array.isArray(hidden) && hidden.includes(groupName)) return false;
  return true;
}

/**
 * Returns the list of toggleable group names that the teacher has hidden.
 * Used by the Visible Assessment Types UI to render checkbox state.
 * Filters out anything not currently toggleable (defensive — old data may
 * reference groups that have since been moved to FROZEN_HIDDEN).
 */
export function getHiddenToggleable(profile) {
  const hidden = profile?.sidebarGroupsHidden;
  if (!Array.isArray(hidden)) return [];
  return hidden.filter(g => TOGGLEABLE_GROUPS.includes(g));
}
